import { BLUE_600, CORAL_100, CORAL_600, GRAY_100, GRAY_400, GRAY_800, GRAY_900, WHITE } from 'constants/styling/theme'
import { Box, ButtonBase, InputBase, NativeSelect, Stack, SxProps, inputBaseClasses, nativeSelectClasses, styled } from '@mui/material'
import { ChangeEvent, KeyboardEvent, MouseEvent, ReactNode, useMemo } from 'react'

import { BorderBoxWrapper } from '../BorderBoxWrapper/BorderBoxWrapper.component'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import { KeyboardEventKey } from 'constants/misc'
import { Label } from '../Label'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'

export enum DropdownInputTypes {
  SELECTOR,
  TEXT,
  SEARCH,
}

/**
 * @typedef InputType Available input types
 */
export type InputType = DropdownInputTypes.SELECTOR | DropdownInputTypes.TEXT | DropdownInputTypes.SEARCH

/**
 * @interface Props Input properties
 */
export interface Props {
  /** Dropdown input id */
  id?: string
  /** Dropdown input type */
  type?: InputType
  /** Dropdown input value */
  value?: string | null
  /** Dropdown input placeholder */
  placeholder?: string
  /** Whether the dropdown input is read only or not */
  readOnly?: boolean
  /** Dropdown input label */
  label?: string
  /** Whether th ropdown input is error */
  isError?: boolean
  /** Dropdown input error text */
  errorText?: string
  /** Whether the dropdown input is disabled or not */
  disabled?: boolean
  /** Whether the dropdown input is required or not */
  required?: boolean
  /** Custom component in input such as a list of badges. */
  customInputContent?: ReactNode
  /** Custom tooltip besides label */
  tooltip?: ReactNode
  /** Whether the dropdown input is a native select */
  isNativeSelect?: boolean
  /** Native select only: the chevron is drawn over the select, which stays the only tab stop and takes its clicks */
  isChevronDecorative?: boolean
  /** Dropdown input children specific to native select */
  children?: ReactNode
  /** Extra optional style */
  sx?: SxProps
  /** OnChange action to update the input value */
  onChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void
  /** OnClick action in input icon */
  onClick?: (event: MouseEvent<HTMLElement>) => void
  /** Callback function to handle blur events. */
  onBlur?: () => void
  /** Callback function to handle key down events. */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
}

// Globle old input styles will overwrite most of the mui input styles. When we redesign and migrate all the old input, we can remove the !important attribute.
const StyledMUIInput = styled(InputBase)(({ error }) => {
  return {
    width: '100%',
    flex: '1 1 6rem',
    [`.${inputBaseClasses.input}`]: {
      height: '2.4rem !important',
      padding: '0 !important',
      border: 'unset !important',
      color: `${error ? CORAL_600 : GRAY_900} !important`,
      backgroundColor: 'transparent !important',
      boxShadow: 'unset !important',
      ':focus': {
        color: `${GRAY_900} !important`
      }
    },
  }
})

const StyledIconButton = styled(ButtonBase)({
  color: GRAY_800,
  padding: '.8rem',
  height: '3.6rem'
})

const StyledRightIconButton = styled(ButtonBase)({
  color: GRAY_800,
  height: '3.6rem',
  padding: '.8rem 0 .8rem .8rem',
})

const DecorativeChevron = styled(Box)({
  position: 'absolute',
  top: 0,
  right: 0,
  display: 'flex',
  alignItems: 'center',
  color: GRAY_800,
  padding: '.8rem',
  height: '3.6rem',
  boxSizing: 'border-box',
  pointerEvents: 'none',
})

const NoArrowNativeSelect = styled(NativeSelect)({
  '& select': {
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    background: 'none',
    backgroundImage: 'none !important',
  },
})

/**
 * @component MUI Dropdown Input
 * @example
 * <MUIDropdownInput
 *  type="DropdownInputTypes.SELECTOR"
 *  value="owo!"
 *  label="text"
 *  onChange={() => {}}
 *  tooltip={
 *    <MUITooltip content="You are awesome!">
 *      <Icon fontSize="medium" sx={{ paddingTop: '.4rem' }}/>
 *    </MUITooltip>
 *  }
 * />
 */
export const MUIDropdownInput: React.FC<Props> = ({
  id,
  type = DropdownInputTypes.SELECTOR,
  value,
  placeholder,
  readOnly = false,
  label = undefined,
  isError = false,
  errorText,
  disabled,
  sx = {},
  required,
  tooltip,
  isNativeSelect = false,
  isChevronDecorative = false,
  children,
  customInputContent,
  onChange,
  onClick,
  onBlur,
  onKeyDown,
}) => {

  const hoverBorderColor = useMemo(() => {
    if (isError) return 'inherit'
    if (disabled) return GRAY_400
    return BLUE_600
  }, [disabled, isError])

  const inputBgColor = useMemo(() => {
    if (isError) return CORAL_100
    if (disabled) return GRAY_100
    return WHITE
  }, [disabled, isError])

  const inputEndIcon = useMemo(() => {
    switch (type) {
      case DropdownInputTypes.SELECTOR:
        return <KeyboardArrowDownRoundedIcon />
      case DropdownInputTypes.TEXT:
        return <HelpOutlineRoundedIcon />
      default:
        return <></>
    }
  }, [type])

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === KeyboardEventKey.ENTER) e.preventDefault()
    onKeyDown?.(e)
  }

  return (
    <Stack gap={.6} boxSizing="border-box" padding={type === DropdownInputTypes.SEARCH ? '0 0.2rem' : '0'}>

      {!!label &&
        <Label
          text={label}
          tooltip={tooltip}
          isError={isError}
          required={required}
        />
      }

      <BorderBoxWrapper
        borderColor={isError ? CORAL_600 : GRAY_400}
        backgroundColor={inputBgColor}
        component="form"
        tabIndex={isChevronDecorative ? undefined : 0}
        onClick={onClick}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          transition: '.3s ease',
          '&:hover': {
            border: `.1rem solid ${hoverBorderColor}`,
          },
          ':has(input:focus)': {
            backgroundColor: WHITE,
            boxShadow: `${type === DropdownInputTypes.SEARCH ? '0px 0px 0px 2px #DEF0F3' : 'none'}`,
          },
          ...sx
        }}
      >

        {type === DropdownInputTypes.SEARCH &&
          <StyledRightIconButton disableRipple>
            <SearchOutlinedIcon sx={{ fontSize: '1.8rem', color: GRAY_800 }} />
          </StyledRightIconButton>
        }

        <Stack gap=".4rem" flexDirection="row" flexWrap="wrap" padding={isChevronDecorative ? '.6rem 0 .6rem 1.2rem' : '.6rem 1.2rem'} width="100%">

          {customInputContent}

          {isNativeSelect ?
            <NoArrowNativeSelect
              id={id}
              value={value}
              IconComponent={() => null}
              onChange={onChange}
              {...(isChevronDecorative && {
                // the select runs under the chevron so a click on it opens the select; its text keeps clear of it
                sx: { [`& .${nativeSelectClasses.select}.${inputBaseClasses.input}`]: { paddingRight: '4rem !important' } },
              })}
              input={
                <StyledMUIInput
                  id={id}
                  value={value}
                  error={isError}
                  disabled={disabled}
                  placeholder={placeholder}
                  readOnly={readOnly}
                  onBlur={onBlur}
                  onKeyDown={handleKeyDown}
                />
              }
            >
              {children}
            </NoArrowNativeSelect>
            :
            <StyledMUIInput
              id={id}
              value={value}
              error={isError}
              disabled={disabled}
              placeholder={placeholder}
              readOnly={readOnly}
              onChange={onChange}
              onBlur={onBlur}
              onKeyDown={handleKeyDown}
            />
          }

        </Stack>

        {isChevronDecorative ?
          <DecorativeChevron component="span" aria-hidden="true">
            {inputEndIcon}
          </DecorativeChevron>
          :
          <StyledIconButton disableRipple>
            {inputEndIcon}
          </StyledIconButton>
        }

      </BorderBoxWrapper>

      {isError && errorText &&
        <Label text={errorText} isError={true} />
      }

    </Stack >
  )
}
